import React, { useEffect, useRef, useState, useCallback } from 'react';
import { Volume2, VolumeX } from 'lucide-react';

/**
 * AmbientPlayer — floating audio pill in the bottom-left corner.
 * Plays a looped Lamole countryside soundscape (CC0 from BigSoundBank).
 * Browsers block autoplay-with-sound until first user gesture, so we:
 *   1. Try to autoplay silently on mount (works),
 *   2. Attempt to unmute; if the browser blocks, we wait for the first
 *      user gesture anywhere on the page and unmute then.
 *   3. Persist the user preference in localStorage so returning visitors
 *      get their previous choice honoured.
 * A small hint bubble appears once, above the pill, to invite listening
 * on the very first visit if audio couldn't start automatically.
 */
export default function AmbientPlayer() {
  const audioRef = useRef(null);
  const fxRefs = useRef([]);         // pool of foreground <audio> nodes
  const scheduleTimerRef = useRef(null);
  const hintTimerRef = useRef(null);
  const fadeRafRef = useRef(null);
  const playingRef = useRef(false);   // mirrors whether base loop is actually playing

  // Preference: 'on' | 'off'. Default 'on' (user requested sound-on).
  const [pref, setPref] = useState(() => {
    if (typeof window === 'undefined') return 'on';
    return localStorage.getItem('lempole_audio') || 'on';
  });
  const [showHint, setShowHint] = useState(false);
  const [ready, setReady] = useState(false);

  // Fade helper — smooth volume ramp so toggling never clicks/pops.
  const fadeTo = useCallback((target, ms = 900) => {
    const el = audioRef.current;
    if (!el) return;
    cancelAnimationFrame(fadeRafRef.current);
    const from = el.volume;
    const start = performance.now();
    const step = (t) => {
      const p = Math.min(1, (t - start) / ms);
      const v = from + (target - from) * p;
      el.volume = Math.max(0, Math.min(1, v));
      if (p < 1) {
        fadeRafRef.current = requestAnimationFrame(step);
      }
    };
    fadeRafRef.current = requestAnimationFrame(step);
  }, []);

  // Try to start playback. Browsers may block if there's been no gesture.
  const tryStart = useCallback(async () => {
    const el = audioRef.current;
    if (!el) return false;
    try {
      // Set very low initial volume, fade up
      el.volume = 0.0;
      await el.play();
      fadeTo(0.35, 1200);
      playingRef.current = true;
      return true;
    } catch (_) {
      return false;
    }
  }, [fadeTo]);

  // Play a random foreground clip. Volume is randomised so it feels natural.
  const playRandomFx = useCallback(() => {
    if (!playingRef.current) return;
    const pool = fxRefs.current.filter(Boolean);
    if (pool.length === 0) return;
    const el = pool[Math.floor(Math.random() * pool.length)];
    if (!el || !el.paused) return;
    try {
      el.currentTime = 0;
      el.volume = 0.22 + Math.random() * 0.12; // 0.22 – 0.34
      el.volume = Math.max(0, Math.min(1, el.volume));
      const p = el.play();
      if (p && typeof p.catch === 'function') p.catch(() => {/* ignored */});
    } catch (_) {/* ignored */}
  }, []);

  // Recursively schedule the next foreground sound every 60–90 seconds.
  const scheduleNextFx = useCallback(() => {
    clearTimeout(scheduleTimerRef.current);
    const delay = 60000 + Math.random() * 30000; // 60–90s
    scheduleTimerRef.current = setTimeout(() => {
      playRandomFx();
      scheduleNextFx();
    }, delay);
  }, [playRandomFx]);

  // Mount: attempt autoplay if pref is 'on'
  useEffect(() => {
    let mounted = true;

    const el = audioRef.current;
    if (!el) return;

    const onCanPlay = () => setReady(true);
    el.addEventListener('canplay', onCanPlay);

    (async () => {
      if (pref === 'off') return;
      const started = await tryStart();
      if (started) {
        scheduleNextFx();
      } else if (mounted) {
        // Show a gentle hint bubble and wait for the first user gesture.
        setShowHint(true);
        const kick = async () => {
          const ok = await tryStart();
          if (ok) {
            scheduleNextFx();
            setShowHint(false);
            document.removeEventListener('pointerdown', kick);
            document.removeEventListener('keydown', kick);
            document.removeEventListener('touchstart', kick);
          }
        };
        document.addEventListener('pointerdown', kick, { once: false });
        document.addEventListener('keydown', kick, { once: false });
        document.addEventListener('touchstart', kick, { once: false });

        // Auto-hide hint after 10s so it doesn't linger
        hintTimerRef.current = setTimeout(() => setShowHint(false), 10000);
      }
    })();

    return () => {
      mounted = false;
      el.removeEventListener('canplay', onCanPlay);
      cancelAnimationFrame(fadeRafRef.current);
      if (hintTimerRef.current) clearTimeout(hintTimerRef.current);
      if (scheduleTimerRef.current) clearTimeout(scheduleTimerRef.current);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleToggle = useCallback(() => {
    const el = audioRef.current;
    if (!el) return;
    if (pref === 'on') {
      // Fade down then pause
      fadeTo(0, 700);
      setTimeout(() => el.pause(), 750);
      clearTimeout(scheduleTimerRef.current);
      playingRef.current = false;
      // Stop any currently-playing foreground clip
      fxRefs.current.forEach((fx) => { if (fx && !fx.paused) fx.pause(); });
      setPref('off');
      localStorage.setItem('lempole_audio', 'off');
    } else {
      el.play().then(() => {
        fadeTo(0.35, 900);
        playingRef.current = true;
        scheduleNextFx();
      }).catch(() => {/* ignored */});
      setPref('on');
      localStorage.setItem('lempole_audio', 'on');
      setShowHint(false);
    }
  }, [pref, fadeTo, scheduleNextFx]);

  return (
    <>
      <audio
        ref={audioRef}
        src={process.env.PUBLIC_URL + '/audio/countryside.mp3'}
        loop
        preload="auto"
        playsInline
        aria-hidden="true"
      />
      {/* Foreground clips — pruning shears, footsteps on soil.
          Played at random every 60–90s while the base loop is on. */}
      <audio
        ref={(el) => { fxRefs.current[0] = el; }}
        src={process.env.PUBLIC_URL + '/audio/pruner.mp3'}
        preload="auto"
        playsInline
        aria-hidden="true"
      />
      <audio
        ref={(el) => { fxRefs.current[1] = el; }}
        src={process.env.PUBLIC_URL + '/audio/feet-leaves.mp3'}
        preload="auto"
        playsInline
        aria-hidden="true"
      />

      <div className="ambient-player" data-state={pref} data-ready={ready ? '1' : '0'}>
        {showHint && (
          <div className="ambient-hint font-caveat" role="status" aria-live="polite">
            ascolta Lamole
            <span className="ambient-hint__arrow" aria-hidden="true">↓</span>
          </div>
        )}
        <button
          type="button"
          className="ambient-btn"
          onClick={handleToggle}
          aria-label={pref === 'on' ? 'Silenzia i suoni di Lamole' : 'Ascolta i suoni di Lamole'}
          aria-pressed={pref === 'on'}
          data-testid="ambient-audio-toggle"
          title={pref === 'on' ? 'Silenzia' : 'Ascolta Lamole'}
        >
          {pref === 'on' ? (
            <>
              <span className="ambient-wave" aria-hidden="true">
                <span /><span /><span /><span />
              </span>
              <Volume2 size={16} />
            </>
          ) : (
            <VolumeX size={16} />
          )}
        </button>
      </div>
    </>
  );
}
